// src/hooks/useCart.js
import { useState, useEffect, useCallback } from 'react';
import shopApi from '../services/shopApi';

export function useCart() {
  const [cart, setCart] = useState({ items: [], subtotal: 0, totalItems: 0 });
  const [loading, setLoading] = useState(false);

  // Load cart on mount
  useEffect(() => {
    shopApi.getCart()
      .then(r => {
        if (r.success) setCart(r.data.cart || { items: [], subtotal: 0, totalItems: 0 });
      })
      .catch(() => {});
  }, []);

  // addToCart(id, quantity, variantId, country, isListingId)
  // isListingId=true  → id is a listingId  (LIST-PROD-xxx)
  // isListingId=false → id is a productId  (PROD-xxx)
  const addToCart = useCallback(async (id, quantity = 1, variantId = null, country = null, isListingId = false) => {
    setLoading(true);
    try {
      const r = await shopApi.addToCart(id, quantity, variantId, country, isListingId);
      if (r.success) setCart(r.data.cart);
      return r.success;
    } catch (e) {
      console.error('🛒 addToCart error:', e);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateItem = useCallback(async (itemId, quantity) => {
    try {
      const r = await shopApi.updateCartItem(itemId, quantity);
      if (r.success) setCart(r.data.cart);
    } catch {}
  }, []);

  const removeItem = useCallback(async (itemId) => {
    try {
      const r = await shopApi.removeCartItem(itemId);
      if (r.success) setCart(r.data.cart);
    } catch {}
  }, []);

  const clearCart = useCallback(async () => {
    try {
      await shopApi.clearCart();
      setCart({ items: [], subtotal: 0, totalItems: 0 });
    } catch {}
  }, []);

  const mergeCart = useCallback(async (token) => {
    try {
      const r = await shopApi.mergeCart(token);
      if (r.success && r.data?.cart) setCart(r.data.cart);
    } catch {}
  }, []);

  // Computed totals
  const subtotal = cart.subtotal || 0;
  const shipping = cart.items?.length
    ? (cart.items.every(i => i.freeShipping) || subtotal >= 50 ? 0 : 9.99)
    : 0;
  const tax   = Math.round(subtotal * 0.1 * 100) / 100;
  const total = Math.round((subtotal + shipping + tax) * 100) / 100;

  return {
    cart,
    setCart,
    loading,
    addToCart,
    updateItem,
    removeItem,
    clearCart,
    mergeCart,
    totals: { subtotal, shipping, tax, total },
  };
}